import { AfterViewInit, ChangeDetectorRef, Component, forwardRef, Input, inject, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { SharedModule } from '@shared/modules/shared.module';
import { activeLocale } from 'app/core/utils/locale';

interface TimeData {
	dates: Date | Date[];
	startTime: string;
	endTime: string;
}

@Component({
	selector: 'app-datepicker',
	imports: [SharedModule],
	templateUrl: './datepicker.component.html',
	styleUrl: './datepicker.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DatepickerComponent),
			multi: true,
		},
	],
})
export class DatepickerComponent
	extends ControlValueAccessorBase<Date | Date[] | TimeData>
	implements OnInit, AfterViewInit
{
	private cdr = inject(ChangeDetectorRef);
	private translocoService = inject(TranslocoService);
	@Input() placeholder = '';
	@Input() label?: string;
	@Input() size: 'large' | 'medium' | 'small' = 'medium';
	@Input() disabled = false;
	@Input() rangeMode = false;
	@Input() error = false;
	@Input() errorMessage = ''; // Empty = translated default (common.messages.requiredField)
	@Input() inline = false;
	@Input() showWeek = false;
	@Input() view: 'date' | 'week' = 'date';
	@Input() numberOfDays = 7;
	@Input() eventDates: Date[] = [];
	@Input() requestDates: Date[] = [];
	@Input() showTimeInputs = false;
	@Input() useSpecialContainer = false; // Controls if gray background container should be used
	@Input() appendTo: 'body' | null = 'body'; // Append overlay to body to prevent scroll issues
	@Input() inputId = `datepicker-${Math.random().toString(36).substring(2, 9)}`;
	/** Shows a red asterisk after the label and marks the control as aria-required. */
	@Input() required = false;

	selectedDate: Date | Date[] | null = null;
	private lastUserSelectedDate: Date | null = null; // User manually selected date (not overwritten by writeValue)
	startTime = '';
	endTime = '';
	showCalendar = false;
	showTimeSelector = false;
	currentTimeType: 'start' | 'end' = 'start';
	tempHour = 9;
	tempMinute = 0;
	private currentWeekStart: Date = new Date();
	private initialized = false;
	private weekDaysCache: { date: Date; number: number }[] = [];
	private eventsCache = new Map<string, boolean>();

	ngOnInit() {
		// Initialize state when component loads
		if (this.view === 'week') {
			this.initialized = true;
			if (!this.value) {
				this.value = new Date();
			}
			this.currentWeekStart = this.getStartOfWeek(this.value as Date);
			this.updateWeekDaysCache();
		}

		// Calendar closed by default
		this.showCalendar = false;
	}

	ngAfterViewInit() {
		// Ensure initializations happen after view
		if (this.view === 'week' && !this.initialized) {
			this.initialized = true;
			this.cdr.detectChanges();
		}
	}

	private updateWeekDaysCache(): void {
		this.weekDaysCache = this.generateWeekDays();
	}

	private getStartOfWeek(date: Date): Date {
		const start = new Date(date);
		start.setHours(0, 0, 0, 0);
		return start;
	}

	getWeekDays(): { date: Date; number: number }[] {
		if (this.weekDaysCache.length === 0) {
			this.updateWeekDaysCache();
		}
		return this.weekDaysCache;
	}

	private generateWeekDays(): { date: Date; number: number }[] {
		const days: { date: Date; number: number }[] = [];
		const start = new Date(this.currentWeekStart);

		for (let i = 0; i < this.numberOfDays; i++) {
			const date = new Date(start);
			date.setDate(start.getDate() + i);
			days.push({
				date: date,
				number: date.getDate(),
			});
		}

		return days;
	}

	previousWeek(): void {
		const newDate = new Date(this.currentWeekStart);
		newDate.setDate(newDate.getDate() - this.numberOfDays);
		this.currentWeekStart = newDate;
		this.updateWeekDaysCache();
	}

	nextWeek(): void {
		const newDate = new Date(this.currentWeekStart);
		newDate.setDate(newDate.getDate() + this.numberOfDays);
		this.currentWeekStart = newDate;
		this.updateWeekDaysCache();
	}

	selectDay(date: Date): void {
		this.value = date;
		this.onChange(date);
		this.onTouched();
	}

	isSelectedDay(date: Date): boolean {
		if (!this.initialized && this.view === 'week') {
			return false;
		}
		if (!this.value) {
			return false;
		}
		return date.toDateString() === (this.value as Date).toDateString();
	}

	isToday(date: Date): boolean {
		return date.toDateString() === new Date().toDateString();
	}

	hasEvents(date: Date | any): boolean {
		if (!date || !this.eventDates?.length) return false;

		// Convert PrimeNG object to Date if needed
		const targetDate = date instanceof Date ? date : new Date(date.year, date.month, date.day);

		// Normalize target date to start of day
		const normalizedTarget = new Date(targetDate);
		normalizedTarget.setHours(0, 0, 0, 0);
		const dateKey = normalizedTarget.toDateString();

		if (!this.eventsCache.has(dateKey)) {
			const hasEvent = this.eventDates.some((eventDate) => {
				if (!eventDate) return false;
				const normalizedEvent = new Date(eventDate);
				normalizedEvent.setHours(0, 0, 0, 0);
				return normalizedEvent.toDateString() === dateKey;
			});
			this.eventsCache.set(dateKey, hasEvent);
		}
		return this.eventsCache.get(dateKey) ?? false;
	}

	hasRequests(date: Date | any): boolean {
		if (!date || !this.requestDates?.length) return false;

		// Convert PrimeNG object to Date if needed
		const targetDate = date instanceof Date ? date : new Date(date.year, date.month, date.day);

		// Normalize target date to start of day
		const normalizedTarget = new Date(targetDate);
		normalizedTarget.setHours(0, 0, 0, 0);
		const dateKey = normalizedTarget.toDateString();

		return this.requestDates.some((requestDate) => {
			if (!requestDate) return false;
			const normalizedRequest = new Date(requestDate);
			normalizedRequest.setHours(0, 0, 0, 0);
			return normalizedRequest.toDateString() === dateKey;
		});
	}

	private areSameDates(date1: Date, date2: Date): boolean {
		if (!date1 || !date2) return false;

		const d1 = new Date(date1);
		const d2 = new Date(date2);

		d1.setHours(0, 0, 0, 0);
		d2.setHours(0, 0, 0, 0);

		return d1.getTime() === d2.getTime();
	}

	onDateSelect(event: any): void {
		// With time inputs, always single selection
		if (this.showTimeInputs) {
			this.selectedDate = event;
			this.value = event;

			// Save user's manual selection
			if (event instanceof Date) {
				this.lastUserSelectedDate = event;
			}

			// Emit change with applied times if any
			// Pass event directly to avoid overwriting this.selectedDate
			this.emitTimeChange(false, event);

			// Close calendar immediately
			this.showCalendar = false;
			this.cdr.detectChanges();
			return;
		}

		// Inline datepicker logic (when useSpecialContainer is active but without time inputs)
		if (this.useSpecialContainer && !this.showTimeInputs) {
			if (this.rangeMode) {
				// In range mode, PrimeNG can send individual dates or arrays
				if (Array.isArray(event) && event.length === 2 && event[0] && event[1]) {
					// Check if both dates are the same day
					const isSameDay = this.areSameDates(event[0], event[1]);

					if (isSameDay) {
						// Convert to single date if same day
						this.selectedDate = event[0];
						this.value = event[0];
						this.onChange(event[0]);
					} else {
						// Keep as range if different days
						this.selectedDate = event;
						this.value = event;
						this.onChange(event);
					}
					this.onTouched(); // Selection complete
					this.showCalendar = false;
					this.cdr.detectChanges();
				} else if (Array.isArray(event) && event.length === 1) {
					// First date of range selected
					this.selectedDate = event;
					this.value = event;
					this.onChange(event); // Emit to update value but without onTouched
					// Keep calendar open to select second date
				} else if (!Array.isArray(event)) {
					// Single date in range mode - can be first or second selection
					this.selectedDate = [event];
					this.value = [event];
					this.onChange([event]);
					// Keep calendar open to select second date
				}
			} else {
				// Single date mode
				this.selectedDate = event;
				this.value = event;
				this.onChange(event);
				this.onTouched();
				this.showCalendar = false;
				this.cdr.detectChanges();
			}
			return;
		}

		// Normal popup datepicker logic (without time inputs)
		if (this.rangeMode && Array.isArray(event) && event.length === 2) {
			// Check if both dates are the same day
			const isSameDay = this.areSameDates(event[0], event[1]);

			if (isSameDay) {
				// Convert to single date if same day
				this.selectedDate = event[0];
				this.value = event[0];
				this.onChange(event[0]);
			} else {
				// Keep as range if different days
				this.selectedDate = event;
				this.value = event;
				this.onChange(event);
			}
			this.onTouched();
		} else if (this.rangeMode && Array.isArray(event) && event.length === 1) {
			this.selectedDate = event;
			this.value = event;
			// Don't emit change until range is complete
		} else if (!this.rangeMode) {
			this.selectedDate = event;
			this.value = event;
			this.onChange(event);
			this.onTouched();
		}
	}

	override writeValue(value: Date | Date[] | TimeData): void {
		// Handle TimeData format (for loading existing data with times)
		if (value && typeof value === 'object' && 'dates' in value) {
			this.selectedDate = value.dates;
			this.startTime = value.startTime || '';
			this.endTime = value.endTime || '';
			this.value = value.dates;
		}
		// Handle array of dates with times (for specific absences)
		else if (Array.isArray(value) && value.length === 2 && this.showTimeInputs) {
			const [startDate, endDate] = value;

			// Extract base date from first element
			this.selectedDate = startDate;
			this.value = startDate;

			// Extract times as HH:mm (value of the time inputs, parsed back with split(':'), so locale-independent)
			this.startTime = this.toTimeValue(startDate);
			this.endTime = this.toTimeValue(endDate);
		} else {
			this.selectedDate = value as Date | Date[];
			this.value = value as Date | Date[];
		}

		// Ensure changes are reflected in view
		this.cdr.detectChanges();
	}

	onStartTimeChange(event: Event): void {
		const target = event.target as HTMLInputElement;
		this.startTime = target.value;
		this.emitTimeChange(false); // Don't mark as touched when changing time
	}

	onEndTimeChange(event: Event): void {
		const target = event.target as HTMLInputElement;
		this.endTime = target.value;
		this.emitTimeChange(false); // Don't mark as touched when changing time
	}

	private emitTimeChange(markAsTouched = false, dateOverride?: Date): void {
		// Priority: dateOverride > lastUserSelectedDate > selectedDate
		const dateToUse = dateOverride || this.lastUserSelectedDate || this.selectedDate;

		if (this.showTimeInputs && dateToUse) {
			// Create new dates with applied hours in UTC
			const baseDate = dateToUse instanceof Date ? dateToUse : new Date();

			// Extract date components from the selected Date object
			// Using getFullYear(), getMonth(), getDate() that work with the local date of the object
			const year = baseDate.getFullYear();
			const month = baseDate.getMonth();
			const day = baseDate.getDate();

			// Create new dates in UTC with extracted components
			const startDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
			const endDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));

			// Apply start time in UTC to avoid timezone issues
			if (this.startTime) {
				const [startHour, startMinute] = this.startTime.split(':');
				startDate.setUTCHours(Number.parseInt(startHour, 10), Number.parseInt(startMinute, 10), 0, 0);
			} else {
				startDate.setUTCHours(0, 0, 0, 0);
			}

			// Apply end time in UTC to avoid timezone issues
			if (this.endTime) {
				const [endHour, endMinute] = this.endTime.split(':');
				endDate.setUTCHours(Number.parseInt(endHour, 10), Number.parseInt(endMinute, 10), 0, 0);
			} else {
				endDate.setUTCHours(23, 59, 59, 999);
			}

			// Send dates with applied times directly (not as TimeData)
			this.onChange([startDate, endDate]);

			// Only mark as touched when selection is confirmed
			if (markAsTouched) {
				this.onTouched();
			}
		}
	}

	toggleCalendar(): void {
		this.showCalendar = !this.showCalendar;

		// When opening calendar with a range selected,
		// reset state to allow new selection
		if (this.showCalendar && this.rangeMode && Array.isArray(this.selectedDate)) {
			// Don't reset completely, keep value to display it
			// but allow new selection
		}
	}

	openTimeSelector(type: 'start' | 'end'): void {
		this.currentTimeType = type;
		const currentTime = type === 'start' ? this.startTime : this.endTime;

		// Initialize with current time or defaults
		if (currentTime) {
			const [hour, minute] = currentTime.split(':');
			this.tempHour = Number.parseInt(hour, 10);
			this.tempMinute = Number.parseInt(minute, 10);
		} else {
			this.tempHour = type === 'start' ? 9 : 17;
			this.tempMinute = 0;
		}

		this.showTimeSelector = true;
	}

	closeTimeSelector(): void {
		this.showTimeSelector = false;
	}

	confirmTimeSelection(): void {
		const timeString = `${this.formatNumber(this.tempHour)}:${this.formatNumber(this.tempMinute)}`;

		if (this.currentTimeType === 'start') {
			this.startTime = timeString;
		} else {
			this.endTime = timeString;
		}

		// Emit change without marking as touched
		this.emitTimeChange(false);
		this.closeTimeSelector();
	}

	incrementHour(): void {
		this.tempHour = this.tempHour >= 23 ? 0 : this.tempHour + 1;
	}

	decrementHour(): void {
		this.tempHour = this.tempHour <= 0 ? 23 : this.tempHour - 1;
	}

	incrementMinute(): void {
		this.tempMinute = this.tempMinute >= 59 ? 0 : this.tempMinute + 1;
	}

	decrementMinute(): void {
		this.tempMinute = this.tempMinute <= 0 ? 59 : this.tempMinute - 1;
	}

	formatNumber(num: number): string {
		return num.toString().padStart(2, '0');
	}

	getFormattedDateRange(): string {
		if (!this.selectedDate && !this.value) return '';

		// Use value as fallback if selectedDate not available
		const dateToFormat = this.selectedDate || this.value;

		if (!dateToFormat) return '';

		// For time inputs, always show single date
		if (this.showTimeInputs && dateToFormat instanceof Date) {
			return this.formatDate(dateToFormat);
		}

		// For range mode - show complete range or single date as appropriate
		if (Array.isArray(dateToFormat) && dateToFormat.length === 2) {
			const [start, end] = dateToFormat;
			if (start && end) {
				const startFormatted = this.formatDate(start);
				const endFormatted = this.formatDate(end);
				return `${startFormatted} - ${endFormatted}`;
			}
		}

		// For incomplete range (only first date selected)
		if (Array.isArray(dateToFormat) && dateToFormat.length === 1 && dateToFormat[0]) {
			return this.formatDate(dateToFormat[0]);
		}

		// For single date
		if (dateToFormat instanceof Date) {
			return this.formatDate(dateToFormat);
		}

		return '';
	}

	private toTimeValue(date: Date): string {
		return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
	}

	private formatDate(date: Date): string {
		return date.toLocaleDateString(activeLocale(this.translocoService), {
			day: '2-digit',
			month: '2-digit',
			year: '2-digit',
		});
	}
}
